import i18n from '@/utils/i18n';
import React from 'react';
import { ACCOUNT_LIST_ROW_MIN_HEIGHT } from '@/experiences/sales-portal/domain/accounts/screens/account-list/components/account-list-item-row';
import { AVATAR_SIZES } from '@usepepper/ui/atoms/avatar';
import { createStyles } from '@/contexts/theme-context';
import { Divider } from '@usepepper/ui/atoms/divider';
import {
  ESTIMATED_HEADER_HEIGHT,
  ESTIMATED_ROW_HEIGHT,
  LAST_FULFILLED_COLUMN_PERCENTAGE,
  NAME_COLUMN_PERCENTAGE,
} from '@/experiences/sales-portal/domain/accounts/screens/account-list/components/account-list-table/account-list-table-columns';
import { isWeb } from '@/utils/navigation';
import { LayoutSize, useLayoutContext } from '@/contexts/layout-context';
import { Skeleton, usePageLayoutHeaderInset } from '@usepepper/sprout-kit-ui';
import { View, ViewStyle } from 'react-native';

const AVATAR_SIZE = AVATAR_SIZES.medium;
const FILTER_PILL_HEIGHT = 24;
const FILTER_PILL_WIDTH = 68;
const HEADER_TEXT_HEIGHT = 12;
const SKELETON_ROW_COUNT = 8;
const SEARCH_BAR_HEIGHT = 40;
const DATE_HEADER_WIDTH = 88;
const NAME_HEADER_WIDTH = 40;
const NAME_CELL_HEIGHT = 14;
const NAME_CELL_DESCRIPTION_HEIGHT = 12;
const NAME_CELL_WIDTH = 140;
const NAME_CELL_DESCRIPTION_WIDTH = 200;
const DATE_CELL_HEIGHT = 14;
const DATE_CELL_WIDTH = 80;
const MOBILE_DATE_WIDTH = 48;

const getRowFadeStyle = (index: number): ViewStyle => ({
  opacity: 1 - index * 0.1,
});

const AccountListSkeletonIdentity: React.FC = () => {
  const [styles, theme] = useStyles();

  return (
    <>
      <Skeleton
        borderRadius={AVATAR_SIZE / 2}
        height={AVATAR_SIZE}
        loadingLabel=""
        width={AVATAR_SIZE}
      />
      <View style={styles.nameText}>
        <Skeleton
          borderRadius={theme.sk.radii.radiusMedium}
          height={NAME_CELL_HEIGHT}
          loadingLabel=""
          width={NAME_CELL_WIDTH}
        />
        <Skeleton
          borderRadius={theme.sk.radii.radiusMedium}
          height={NAME_CELL_DESCRIPTION_HEIGHT}
          loadingLabel=""
          width={NAME_CELL_DESCRIPTION_WIDTH}
        />
      </View>
    </>
  );
};

export const AccountListSkeletonRow: React.FC<{ style?: ViewStyle }> = ({
  style,
}) => {
  const [styles, theme] = useStyles();

  return (
    <View style={style}>
      <View style={styles.bodyRow}>
        <View style={styles.nameCell}>
          <AccountListSkeletonIdentity />
        </View>
        <View style={styles.dateCell}>
          <Skeleton
            borderRadius={theme.sk.radii.radiusMedium}
            height={DATE_CELL_HEIGHT}
            loadingLabel=""
            width={DATE_CELL_WIDTH}
          />
        </View>
      </View>
      <Divider />
    </View>
  );
};

export const AccountListMobileSkeletonRow: React.FC<{ style?: ViewStyle }> = ({
  style,
}) => {
  const [styles, theme] = useStyles();

  return (
    <View style={style}>
      <View style={styles.mobileRow}>
        <AccountListSkeletonIdentity />
        <Skeleton
          alignSelf="flex-start"
          borderRadius={theme.sk.radii.radiusMedium}
          height={DATE_CELL_HEIGHT}
          loadingLabel=""
          width={MOBILE_DATE_WIDTH}
        />
      </View>
      <Divider />
    </View>
  );
};

export const AccountListSkeletonRows: React.FC<{
  count: number;
  style?: ViewStyle;
}> = ({ count, style }) => {
  const [styles] = useStyles();
  const { layoutSize } = useLayoutContext();
  const isMobileBottomNavLayout = !isWeb && layoutSize === LayoutSize.SMALL;
  const SkeletonRow = isMobileBottomNavLayout
    ? AccountListMobileSkeletonRow
    : AccountListSkeletonRow;

  return (
    <View
      accessibilityRole="progressbar"
      accessibilityState={{ busy: true }}
      style={[style, isMobileBottomNavLayout ? styles.mobileRowsInset : null]}
    >
      {Array.from({ length: count }).map((_, rowIndex) => (
        <SkeletonRow key={rowIndex} />
      ))}
    </View>
  );
};

export const AccountListSkeleton: React.FC = () => {
  const [styles, theme] = useStyles();
  const { layoutSize } = useLayoutContext();
  const isMobileBottomNavLayout = !isWeb && layoutSize === LayoutSize.SMALL;
  const headerInset = usePageLayoutHeaderInset();

  if (isMobileBottomNavLayout) {
    return (
      <View
        accessibilityLabel={i18n.t('accounts')}
        accessibilityRole="progressbar"
        accessibilityState={{ busy: true }}
        style={[styles.container, { paddingTop: headerInset }]}
      >
        <View style={styles.tableContainer}>
          {Array.from({ length: SKELETON_ROW_COUNT }).map((_, rowIndex) => (
            <AccountListMobileSkeletonRow
              key={rowIndex}
              style={getRowFadeStyle(rowIndex)}
            />
          ))}
        </View>
      </View>
    );
  }

  return (
    <View
      accessibilityLabel={i18n.t('accounts')}
      accessibilityRole="progressbar"
      accessibilityState={{ busy: true }}
      style={styles.container}
    >
      <Skeleton
        borderRadius={theme.sk.radii.radiusMedium}
        height={SEARCH_BAR_HEIGHT}
        loadingLabel=""
        width="100%"
      />

      <View style={styles.filtersRow}>
        <Skeleton
          borderRadius={theme.sk.radii.radiusRound}
          height={FILTER_PILL_HEIGHT}
          loadingLabel=""
          width={FILTER_PILL_WIDTH}
        />
        <Skeleton
          borderRadius={theme.sk.radii.radiusRound}
          height={FILTER_PILL_HEIGHT}
          loadingLabel=""
          width={FILTER_PILL_WIDTH}
        />
      </View>

      <View style={styles.tableContainer}>
        <View style={styles.headerRow}>
          <View style={styles.headerNameCell}>
            <Skeleton
              borderRadius={theme.sk.radii.radiusMedium}
              height={HEADER_TEXT_HEIGHT}
              loadingLabel=""
              width={NAME_HEADER_WIDTH}
            />
          </View>
          <View style={styles.headerDateCell}>
            <Skeleton
              borderRadius={theme.sk.radii.radiusMedium}
              height={HEADER_TEXT_HEIGHT}
              loadingLabel=""
              width={DATE_HEADER_WIDTH}
            />
          </View>
        </View>
        {Array.from({ length: SKELETON_ROW_COUNT }).map((_, rowIndex) => (
          <AccountListSkeletonRow
            key={rowIndex}
            style={getRowFadeStyle(rowIndex)}
          />
        ))}
      </View>
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  bodyRow: {
    alignItems: 'center',
    flexDirection: 'row',
    minHeight: ESTIMATED_ROW_HEIGHT,
  },
  container: {
    flex: 1,
    gap: theme.sk.spacing.spaceSMedium,
    paddingTop: theme.sk.spacing.spaceLarge,
  },
  dateCell: {
    flex: LAST_FULFILLED_COLUMN_PERCENTAGE,
    paddingHorizontal: theme.sk.spacing.spaceMedium,
  },
  filtersRow: {
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSmall,
    paddingBottom: theme.sk.spacing.spaceSmall,
  },
  headerDateCell: {
    flex: LAST_FULFILLED_COLUMN_PERCENTAGE,
    justifyContent: 'center',
    paddingHorizontal: theme.sk.spacing.spaceMedium,
    paddingVertical: theme.sk.spacing.spaceSMedium,
  },
  headerNameCell: {
    flex: NAME_COLUMN_PERCENTAGE,
    justifyContent: 'center',
    paddingHorizontal: theme.sk.spacing.spaceMedium,
    paddingVertical: theme.sk.spacing.spaceSMedium,
  },
  headerRow: {
    borderBottomWidth: 1,
    borderColor: theme.sk.color.border.primary,
    flexDirection: 'row',
    minHeight: ESTIMATED_HEADER_HEIGHT,
  },
  mobileRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceMedium,
    minHeight: ACCOUNT_LIST_ROW_MIN_HEIGHT,
    paddingTop: theme.sk.spacing.spaceSmall,
  },
  mobileRowsInset: {
    paddingHorizontal: theme.sk.spacing.spaceMedium,
  },
  nameCell: {
    alignItems: 'center',
    flex: NAME_COLUMN_PERCENTAGE,
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceMedium,
    paddingHorizontal: theme.sk.spacing.spaceMedium,
  },
  nameText: {
    flex: 1,
    gap: theme.sk.spacing.spaceXsmall,
  },
  tableContainer: {
    flex: 1,
  },
}));
